import { Component, OnInit, Injector } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { Printer, Store, Ticket } from '../../models/RestModels';
import { GetEmpty } from '../../classes/Empties';
import { PrinterService, PrintResult, ENCODER_COLUMN_WIDTHS, resolveFontBColumns, snapEncoderColumns } from '../../services/printer.service';
import { PrintUtils } from '../../classes/PrintUtils';
import {
	PaymentReceiptTicketBuilder,
	sampleMultiOrderReceiptData,
	sampleSingleOrderReceiptData
} from '../../classes/PaymentReceiptTicketBuilder';
import { forkJoin, of } from 'rxjs';

@Component({
	selector: 'app-save-printer-config',
	templateUrl: './save-printer-config.component.html',
	styleUrls: ['./save-printer-config.component.css']
})
export class SavePrinterConfigComponent extends BaseComponent implements OnInit {

	printer: Printer = GetEmpty.printer();
	store_list: Store[] = [];
	ticket_list: Ticket[] = [];

	// Connection status
	connection_status: 'disconnected' | 'connecting' | 'connected' | 'error' = 'disconnected';
	connection_message: string = '';

	// Print status
	print_status: 'idle' | 'printing' | 'success' | 'error' = 'idle';
	print_message: string = '';

	// Preview XML
	preview_xml: string = '';
	preview_kind: 'generic' | 'single' | 'multi' = 'generic';

	// Printer models/flavors available
	printer_models: { value: string, label: string }[] = [
		{ value: 'bixolon-srp350iii', label: 'Bixolon SRP-350III' },
		{ value: 'bixolon-srp350plusiii', label: 'Bixolon SRP-350plusIII' },
		{ value: 'epson-tm-t88', label: 'Epson TM-T88' },
		{ value: 'epson-tm-t20', label: 'Epson TM-T20' },
		{ value: 'star-tsp100', label: 'Star TSP100' },
		{ value: 'star-tsp650', label: 'Star TSP650' },
		{ value: 'pos-5890', label: 'POS-5890 (Generic 58mm)' },
		{ value: 'pos-8350', label: 'POS-8350 (Generic 80mm)' },
	];

	// Codepage mappings available
	codepage_mappings: { value: string, label: string }[] = [
		{ value: 'bixolon', label: 'Bixolon' },
		{ value: 'epson', label: 'Epson' },
		{ value: 'star', label: 'Star' },
		{ value: 'citizen', label: 'Citizen' },
		{ value: 'zjiang', label: 'Zjiang' },
	];

	// Common USB vendor/product IDs
	common_printers: { vendor_id: string, product_id: string, name: string }[] = [
		{ vendor_id: '0x1504', product_id: '0x003d', name: 'Bixolon SRP-350' },
		{ vendor_id: '0x04b8', product_id: '0x0202', name: 'Epson TM-T88' },
		{ vendor_id: '0x04b8', product_id: '0x0e15', name: 'Epson TM-T20' },
		{ vendor_id: '0x0519', product_id: '0x0003', name: 'Star TSP100' },
		{ vendor_id: '0x0416', product_id: '0x5011', name: 'POS80 Series 80mm' },
		{ vendor_id: '0x0483', product_id: '0x5740', name: 'Generic USB Printer' },
	];

	private printerService: PrinterService;

	constructor(public injector: Injector) {
		super(injector);
		this.printerService = injector.get(PrinterService);
	}

	ngOnInit() {
		this.setTitle('Configuracion de Impresora USB');

		this.is_loading = true;

		this.subs.sink = this.route.paramMap.subscribe(params => {
			const printerId = params.has('id') ? parseInt(params.get('id') as string) : 0;

			this.subs.sink = forkJoin({
				printer: printerId ? this.rest.printer.get(printerId) : of(GetEmpty.printer()),
				stores: this.rest.store.search({ limit: 9999, sort_order: ['name_ASC'] }),
				tickets: this.rest.ticket.search({ limit: 9999, sort_order: ['name_ASC'] })
			}).subscribe(
				(responses) => {
					this.is_loading = false;
					this.printer = responses.printer;
					this.store_list = responses.stores.data;
					this.ticket_list = responses.tickets.data;

					this.route.queryParamMap.subscribe(queryParams => {
						if (!printerId) {
							if (queryParams.get('vendor_id')) {
								this.printer.vendor_id = queryParams.get('vendor_id') || '';
							}
							if (queryParams.get('product_id')) {
								this.printer.product_id = queryParams.get('product_id') || '';
							}
						}
					});

					if (!this.printer.store_id && this.store_list.length > 0) {
						this.printer.store_id = this.rest.current_user?.store_id || this.store_list[0].id;
					}

					this.updatePreview();
				},
				(error) => {
					this.is_loading = false;
					this.showError('Error al cargar la configuracion');
				}
			);
		});
	}

	/**
	 * Apply common printer preset
	 */
	applyPreset(preset: { vendor_id: string, product_id: string, name: string }) {
		this.printer.vendor_id = preset.vendor_id;
		this.printer.product_id = preset.product_id;
		if (!this.printer.name) {
			this.printer.name = preset.name;
		}
	}

	get font_a_columns_warning(): string {
		if (!this.printer || ENCODER_COLUMN_WIDTHS.includes(this.printer.font_a_columns)) {
			return '';
		}
		return `Valor no soportado por la impresora, se usará ${snapEncoderColumns(this.printer.font_a_columns)} al imprimir.`;
	}

	updatePreview() {
		// The USB encoder line width comes from buildEncoderConfig, so the
		// preview must use the same value or the table can overflow the line.
		const columns = this.printerService.buildEncoderConfig(this.printer as Printer).columns;

		if (this.preview_kind === 'generic') {
			const printUtils = new PrintUtils(1);
			const storeName = this.store_list.find(s => s.id === this.printer.store_id)?.name || 'TIENDA';
			this.preview_xml = printUtils.createTestTicketXml(storeName, columns);
			return;
		}

		const ticket = this.ticket_list.find(t => t.id === this.printer.ticket_id)
			|| this.rest.current_ticket
			|| null;
		const data = this.preview_kind === 'single'
			? sampleSingleOrderReceiptData()
			: sampleMultiOrderReceiptData();
		const builder = new PaymentReceiptTicketBuilder(
			data,
			columns,
			resolveFontBColumns(this.printer as Printer, columns),
			ticket
		);
		this.preview_xml = builder.buildXml();
	}

	setPreviewKind(kind: 'generic' | 'single' | 'multi') {
		this.preview_kind = kind;
		this.updatePreview();
	}

	/**
	 * Test USB connection to printer
	 * Must be triggered by user click due to WebUSB security
	 */
	async testConnection(event: Event) {
		this.connection_status = 'connecting';
		this.connection_message = 'Conectando...';

		try {
			const result: PrintResult = await this.printerService.testConnection(this.printer as Printer, event);

			if (result.success) {
				this.connection_status = 'connected';
				this.connection_message = result.message;
				this.showSuccess('Impresora conectada correctamente');
			} else {
				this.connection_status = 'error';
				this.connection_message = result.message + (result.error ? `: ${result.error}` : '');
				this.showError('Error al conectar: ' + result.message);
			}
		} catch (error: any) {
			this.connection_status = 'error';
			this.connection_message = error?.message || 'Error desconocido';
			this.showError('Error al conectar: ' + this.connection_message);
		}
	}

	/**
	 * Print test ticket
	 * Must be triggered by user click due to WebUSB security
	 */
	async printTestTicket(event: Event) {
		this.print_status = 'printing';
		this.print_message = 'Imprimiendo...';

		try {
			const result: PrintResult = await this.printerService.print(
				this.preview_xml,
				this.printer as Printer,
				event
			);

			if (result.success) {
				this.print_status = 'success';
				this.print_message = result.message;
				this.showSuccess('Ticket impreso correctamente');
			} else {
				this.print_status = 'error';
				this.print_message = result.message + (result.error ? `: ${result.error}` : '');
				this.showError('Error al imprimir: ' + result.message);
			}
		} catch (error: any) {
			this.print_status = 'error';
			this.print_message = error?.message || 'Error desconocido';
			this.showError('Error al imprimir: ' + this.print_message);
		}
	}

	/**
	 * Save printer configuration
	 */
	save() {
		this.is_loading = true;

		// Ensure required fields
		if (!this.printer.name) {
			this.showError('El nombre de la impresora es requerido');
			this.is_loading = false;
			return;
		}

		if (!this.printer.vendor_id || !this.printer.product_id) {
			this.showError('Vendor ID y Product ID son requeridos');
			this.is_loading = false;
			return;
		}

		console.log('Saving printer:', this.printer);
		console.log('Vendor ID to save:', this.printer.vendor_id);
		console.log('Product ID to save:', this.printer.product_id);

		const observable = this.printer.id
			? this.rest.printer.update(this.printer as Printer)
			: this.rest.printer.create(this.printer as Printer);

		this.subs.sink = observable.subscribe(
			(response) => {
				this.is_loading = false;
				this.printer = response;
				console.log('Saved response:', response);
				this.showSuccess('Configuracion guardada correctamente');
			},
			(error) => {
				this.is_loading = false;
				this.showError('Error al guardar: ' + error?.message);
			}
		);
	}
}
